import { useMemo, useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Archive, BriefcaseBusiness, Building2, CalendarDays, CircleDollarSign, ContactRound, Gift, Inbox, ListTodo, LockKeyhole, MailOpen, MailPlus, Megaphone, MessageSquareText, Package, PanelsTopLeft, Send, Sparkles, Star, Sun, Tags, Trash2, UsersRound, MoonStar } from "lucide-react";
import { Menu, Button, SelectBox, FocusSidebar, type ShellFocusArea, type ShellLink } from "@/ui";
import { getSelectedProduct, setSelectedProduct, subscribeSelectedProduct } from "@/lib/prefs";
import { mailbox, AUTH_ORIGIN, type ProductKey } from "@/lib/api";
import { MAILBOX_LABELS_QUERY_KEY } from "@/lib/query-keys";
import { useMailUnreadCount } from "@/lib/use-mail-unread-count";

export const CRM_FOCUS_AREAS: readonly ShellFocusArea[] = [
    { id: "workday", label: "Workday", icon: Sun, items: [
        { to: "/today", label: "Today", icon: Sun },
        { to: "/actions", label: "Tasks", icon: ListTodo },
        { to: "/quiet", label: "Going quiet", icon: MoonStar },
        { to: "/calendar", label: "Calendar", icon: CalendarDays },
        { to: "/meetings", label: "Meetings", icon: UsersRound },
    ] },
    { id: "mail", label: "Mail", icon: Inbox, items: [
        { to: "/mail/personal", label: "My Gmail", icon: LockKeyhole },
        { to: "/inbox", label: "Inbox", icon: Inbox }, { to: "/sent", label: "Sent", icon: Send },
        { to: "/mail/starred", label: "Starred", icon: Star }, { to: "/mail/all", label: "All Mail", icon: MailOpen },
        { to: "/mail/archive", label: "Archive", icon: Archive }, { to: "/mail/trash", label: "Trash", icon: Trash2 },
        { to: "/campaigns/new", label: "Campaign", icon: Megaphone }, { to: "/drafts", label: "Drafts", icon: Archive },
        { to: "/templates", label: "Templates", icon: PanelsTopLeft }, { to: "/mail/labels", label: "Manage labels", icon: Tags },
    ], action: { to: "/inbox", label: "New email", icon: MailPlus, state: { compose: {} } } },
    { id: "collaboration", label: "Chat", icon: MessageSquareText, items: [
        { to: "/chat", label: "Chat", icon: MessageSquareText },
        { to: "/cases", label: "Cases", icon: BriefcaseBusiness },
    ] },
    { id: "sales", label: "Sales", icon: CircleDollarSign, items: [
        { to: "/pipeline", label: "Pipeline", icon: CircleDollarSign }, { to: "/contacts", label: "Contacts", icon: ContactRound },
        { to: "/companies", label: "Companies", icon: Building2 }, { to: "/events", label: "Events", icon: Sparkles },
        { to: "/gifts", label: "Gifts", icon: Gift }, { to: "/shipping", label: "Shipping", icon: Package },
    ] },
] as const;

/** react-router link for the shell; the shell itself stays router-agnostic. */
const RouterLink: ShellLink = ({ to, state, ...props }) => <Link to={to} state={state} {...props} />;

export default function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
    const { pathname } = useLocation();
    const product = useSyncExternalStore(subscribeSelectedProduct, getSelectedProduct);
    const unreadCount = useMailUnreadCount();
    const { data: labelData } = useQuery({
        queryKey: MAILBOX_LABELS_QUERY_KEY,
        queryFn: mailbox.labels,
        staleTime: 60_000,
    });
    const areas = useMemo(() => CRM_FOCUS_AREAS.map(area => area.id === "mail" ? {
        ...area,
        items: [
            ...area.items.map(item => item.to === "/inbox" ? { ...item, count: unreadCount } : item),
            ...(labelData?.labels ?? []).map(label => ({
                to: `/mail/label/${encodeURIComponent(label.id)}`,
                label: label.name,
                icon: Tags,
                count: label.threadCount,
            })),
        ],
    } : area), [labelData?.labels, unreadCount]);
    return (
        <FocusSidebar
            pathname={pathname}
            link={RouterLink}
            areas={areas}
            onNavigate={onNavigate}
            brand={<><div className="brand-mark" aria-hidden="true">K</div><div className="brand-copy"><strong>Kardoe</strong></div></>}
            footer={<>
                <label className="sidebar-product-select">
                    <span>Product</span>
                    <SelectBox
                        value={product}
                        onValueChange={value => setSelectedProduct(value as ProductKey | "")}
                        aria-label="Default product"
                        options={[
                            { value: "", label: "No default" },
                            { value: "live-room", label: "Live Room" },
                            { value: "attend", label: "Attend" },
                        ]}
                    />
                </label>
                <Menu.Root>
                    <Menu.Trigger render={<Button variant="ghost" className="account-trigger" />}><span className="avatar">PS</span><span className="account-copy"><strong>Paul Stenhouse</strong></span><span className="account-more">•••</span></Menu.Trigger>
                    <Menu.Content side="top" align="start" className="w-56"><Menu.Group><Menu.GroupLabel>My account</Menu.GroupLabel><Menu.Item onClick={() => { window.location.href = "/settings/general"; }}>Settings</Menu.Item><Menu.Item onClick={() => { window.location.href = `${AUTH_ORIGIN}/account/profile`; }}>Account settings</Menu.Item></Menu.Group><Menu.Separator /><Menu.Item onClick={() => { window.location.href = `${AUTH_ORIGIN}/`; }}>Sign out</Menu.Item></Menu.Content>
                </Menu.Root>
            </>}
        />
    );
}
